import { useCallback, useState } from 'react';
import { WorkOrderFilterJoin } from '../../../../enums/workOrderFilter';
import type { WorkOrderFilterRule } from '../../../../types/InfrastructureManagerTypes';
import FilterRuleRow from './FilterRuleRow';
import styles from './WorkOrderFilter.module.scss';

const createEmptyRule = (): WorkOrderFilterRule => ({ id: crypto.randomUUID(), join: WorkOrderFilterJoin.AND });

const hasValue = (value: WorkOrderFilterRule['value']) => (Array.isArray(value) ? value.every(Boolean) : Boolean(value));

const isCompleteRule = (rule: WorkOrderFilterRule) => Boolean(rule.category && rule.condition && hasValue(rule.value));

interface FilterPanelProps {
  initialRules: WorkOrderFilterRule[];
  onApply: (rules: WorkOrderFilterRule[]) => void;
  onClose: () => void;
}

const FilterPanel = ({ initialRules, onApply, onClose }: FilterPanelProps) => {
  // Seeded once per open; the popover unmounts on close, so the draft never outlives it.
  const [rules, setRules] = useState(() => (initialRules.length ? initialRules : [createEmptyRule()]));

  const handleRuleChange = useCallback((id: string, patch: Partial<WorkOrderFilterRule>) => {
    setRules((prev) => prev.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)));
  }, []);

  // The last row resets instead, so the panel always keeps one row.
  const handleRuleRemove = useCallback((id: string) => {
    setRules((prev) => (prev.length > 1 ? prev.filter((rule) => rule.id !== id) : [createEmptyRule()]));
  }, []);

  const handleAdd = useCallback(() => setRules((prev) => [...prev, createEmptyRule()]), []);

  const handleApply = () => onApply(rules.filter(isCompleteRule));

  return (
    <div className={styles.filterPanel}>
      <h3 className={styles.filterTitle}>Filter</h3>
      <div className={styles.filterRules}>
        {rules.map((rule, index) => (
          <FilterRuleRow
            key={rule.id}
            rule={rule}
            isFirst={index === 0}
            onChange={handleRuleChange}
            onRemove={handleRuleRemove}
          />
        ))}
      </div>
      <button type="button" className={styles.filterAdd} onClick={handleAdd}>
        Add
      </button>
      <div className={styles.filterActions}>
        <button type="button" className={styles.filterAction} onClick={onClose}>
          Close
        </button>
        <button type="button" className={styles.filterAction} onClick={handleApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;
